import {
  Box,
  Drawer,
  DrawerProps,
  FormControl,
  InputLabel,
  ListItemIcon,
  MenuItem,
  MenuItemProps,
  styled,
  Typography,
  TypographyProps,
} from '@mui/material';

import { Icon } from '@/components/Icon';

import { doNotForwardTheseProps } from '@/styles';

export const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '.MuiFormHelperText-root': {
    fontSize: theme.typography.captionBold.fontSize,
    marginLeft: 0,
    marginRight: 0,

    '&.Mui-error': {
      fontWeight: theme.typography.captionBold.fontWeight,
    },
  },
}));

export const AutocompleteOption = styled(MenuItem)(({ theme }) => ({
  borderRadius: 8,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  minHeight: 32,
  fontSize: theme.typography.body2.fontSize,
  wordWrap: 'break-word',
  whiteSpace: 'wrap',

  ':hover': {
    backgroundColor: theme.palette.neutral[100],
  },
}));

export const DropdownOption = styled(
  (props: MenuItemProps & { shouldMarginTop?: boolean }) => (
    <MenuItem {...props} />
  ),
  {
    shouldForwardProp: doNotForwardTheseProps('marginTop', 'shouldMarginTop'),
  },
)(({ theme, shouldMarginTop }) => ({
  borderRadius: 8,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minHeight: 32,
  fontSize: theme.typography.body2.fontSize,

  ':hover': {
    backgroundColor: theme.palette.neutral[100],
  },

  '.MuiListItemIcon-root': {
    minWidth: 0,

    ':first-of-type': {
      marginRight: theme.spacing(1),
    },
  },

  '.MuiListItemText-root': {
    flex: 1,
    minWidth: 0,
    wordWrap: 'break-word',
    whiteSpace: 'wrap',
  },

  '&.Mui-selected': {
    backgroundColor: 'transparent',

    ':hover, :focus': {
      backgroundColor: 'transparent',
    },
  },

  ':not(:first-of-type)': {
    marginTop: theme.spacing(1),
  },
  [theme.breakpoints.up('lg')]: {
    ':first-of-type': {
      marginTop: shouldMarginTop ? theme.spacing(2) : 0,
    },
  },
}));

export const OptionCheckIcon = styled(ListItemIcon)(() => ({
  '&.MuiListItemIcon-root': {
    minWidth: 0,
  },
}));

export const SelectedIcon = styled(Icon)(({ theme }) => ({
  color: theme.palette.primaries[500],
}));

export const SelectedOption = styled(DropdownOption)(({ theme }) => ({
  backgroundColor: 'unset',
  paddingLeft: 0,
  paddingRight: 0,

  '.MuiListItemIcon-root': {
    marginRight: theme.spacing(1),
  },

  ':hover': {
    backgroundColor: 'unset',
  },
  [theme.breakpoints.up('lg')]: {
    ':first-of-type': {
      marginTop: 0,
    },
  },
}));

export const SelectDrawer = styled((props: DrawerProps) => (
  <Drawer anchor="right" hideBackdrop {...props} />
))(({ theme }) => ({
  '.MuiDrawer-paper': {
    paddingTop: theme.layout.headerHeight.xs,
    width: '100vw',

    '.MuiMenuItem-root': {
      flexShrink: 0,
      height: 48,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),

      ':not(:first-of-type)': {
        margin: 0,
      },
    },
  },
}));

export const SelectDrawerSearchBar = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  position: 'sticky',
  left: 0,
  top: 0,
  zIndex: 1,
  width: '100%',

  [theme.breakpoints.up('lg')]: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    marginBottom: 0,
  },
}));

export const SelectGroup = styled(Box)(({ theme }) => ({
  ':not(:first-of-type)': {
    paddingTop: theme.spacing(3),
  },
}));
export const SelectGroupName = styled((props: TypographyProps) => (
  <Typography className="body2Medium" {...props} />
))(({ theme }) => ({
  marginBottom: theme.spacing(1),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),

  [theme.breakpoints.up('lg')]: {
    marginBottom: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    ':nth-of-type(2)': {
      marginTop: theme.spacing(1),
    },
  },
}));

export const Label = styled(InputLabel)(() => ({
  marginTop: -4,
  '&.MuiInputLabel-shrink': {
    marginTop: 0,
    marginLeft: 0,
  },
}));
